import * as z from 'zod';

export const ListProvidersRequest = z.object({});
export const ListProvidersResponse = z.object({
  providers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('ID of the external calendar provider.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of the external calendar provider.')
          .optional()
          .nullable(),
        calendarType: z
          .enum([
            'UNDEFINED',
            'GOOGLE',
            'I_CAL',
            'OUTLOOK',
            'OFFICE_365',
            'MICROSOFT',
            'OTHER',
          ])
          .describe('External calendar provider type.')
          .optional(),
        features: z
          .object({
            connectMethods: z
              .array(z.enum(['UNDEFINED', 'OAUTH', 'CREDENTIALS']))
              .optional(),
            updateSyncConfig: z
              .boolean()
              .describe(
                'Whether you can update `syncConfic` for all external calendar connections\nby calling [Update Sync Config](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/update-sync-config).'
              )
              .optional()
              .nullable(),
            listEventFromCalendars: z
              .enum([
                'UNDEFINED',
                'NOT_SUPPORTED',
                'PRIMARY_CALENDAR_ONLY',
                'SPECIFIC_CALENDARS',
              ])
              .describe(
                'Information about which events you can import from the external calendar to\nthe Wix site.'
              )
              .optional(),
            syncToCalendar: z
              .enum([
                'UNDEFINED',
                'NOT_SUPPORTED',
                'PRIMARY_CALENDAR_ONLY',
                'SPECIFIC_CALENDAR',
                'DEDICATED_CALENDAR',
              ])
              .describe(
                'Whether you can export Wix calendar [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nto the external calendar.'
              )
              .optional(),
          })
          .describe(
            'Information about how you can connect the external calendar to the Wix site,\nand which functionality it supports.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const GetConnectionRequest = z.object({
  connectionId: z.string().describe('ID of the connection to retrieve.'),
});
export const GetConnectionResponse = z.object({
  connection: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the connection between the external calendar and the Wix site.'
        )
        .optional()
        .nullable(),
      providerId: z
        .string()
        .describe('ID of the external calendar provider.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      calendarType: z
        .enum([
          'UNDEFINED',
          'GOOGLE',
          'I_CAL',
          'OUTLOOK',
          'OFFICE_365',
          'MICROSOFT',
          'OTHER',
        ])
        .describe('External calendar type.')
        .optional(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nthat's connected to the external calendar."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      userId: z
        .string()
        .describe(
          'ID of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the external calendar connection belongs.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app which created the external calendar connection.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalAccountEmail: z
        .string()
        .describe(
          'Email address associated with the external calendar account. Available only\nafter completed successfully.'
        )
        .email()
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNDEFINED',
          'CONNECTED',
          'SYNC_IN_PROGRESS',
          'SYNCED',
          'DISCONNECTED',
          'ERROR',
        ])
        .describe('Connection status.')
        .optional(),
      errorReason: z
        .enum([
          'UNDEFINED',
          'TOKEN_REVOKED',
          'EXTERNAL_CALENDAR_CREATION_FAILED',
          'EXTERNAL_CALENDAR_DELETED',
        ])
        .describe(
          'Reason for the error. Available only if `status` is `ERROR`.'
        )
        .optional(),
      syncConfig: z
        .object({
          listEventFromCalendars: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether you can call [List Events](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/list-events)\nto import events from the external calendar to the Wix site.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({ calendars: z.never().optional() }),
                z.object({
                  calendars: z
                    .object({
                      calendars: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('ID of the external calendar account.')
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe(
                                'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe(
                      'You can import events from the list of specified external calendar\naccounts. The list may include the primary calendar.'
                    ),
                }),
              ])
            )
            .describe(
              'Configuration for importing events from the external calendar to the Wix\nsite.'
            )
            .optional(),
          syncToCalendar: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether Wix [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nare exported to the external calendar.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Configuration for exporting events from the Wix site to the external\ncalendar.'
            )
            .optional(),
        })
        .describe('Settings about which sync operations are supported.')
        .optional(),
    })
    .describe('Retrieved external calendar connection.')
    .optional(),
});
export const ListConnectionsRequest = z.object({
  options: z
    .object({
      scheduleIds: z.array(z.string()).optional(),
      partialFailure: z
        .boolean()
        .describe(
          "Whether to return a partial list of connections if details can't be\nretrieved for all connections.\n\nDefault: `false`"
        )
        .optional()
        .nullable(),
    })
    .describe('Options to use when listing connections.')
    .optional(),
});
export const ListConnectionsResponse = z.object({
  connections: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the connection between the external calendar and the Wix site.'
          )
          .optional()
          .nullable(),
        providerId: z
          .string()
          .describe('ID of the external calendar provider.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        calendarType: z
          .enum([
            'UNDEFINED',
            'GOOGLE',
            'I_CAL',
            'OUTLOOK',
            'OFFICE_365',
            'MICROSOFT',
            'OTHER',
          ])
          .describe('External calendar type.')
          .optional(),
        scheduleId: z
          .string()
          .describe(
            "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nthat's connected to the external calendar."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        userId: z
          .string()
          .describe(
            'ID of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the external calendar connection belongs.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe(
            'ID of the app which created the external calendar connection.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalAccountEmail: z
          .string()
          .describe(
            'Email address associated with the external calendar account. Available only\nafter completed successfully.'
          )
          .email()
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNDEFINED',
            'CONNECTED',
            'SYNC_IN_PROGRESS',
            'SYNCED',
            'DISCONNECTED',
            'ERROR',
          ])
          .describe('Connection status.')
          .optional(),
        errorReason: z
          .enum([
            'UNDEFINED',
            'TOKEN_REVOKED',
            'EXTERNAL_CALENDAR_CREATION_FAILED',
            'EXTERNAL_CALENDAR_DELETED',
          ])
          .describe(
            'Reason for the error. Available only if `status` is `ERROR`.'
          )
          .optional(),
        syncConfig: z
          .object({
            listEventFromCalendars: z
              .intersection(
                z.object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether you can call [List Events](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/list-events)\nto import events from the external calendar to the Wix site.'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({ calendars: z.never().optional() }),
                  z.object({
                    calendars: z
                      .object({
                        calendars: z
                          .array(
                            z.object({
                              _id: z
                                .string()
                                .describe(
                                  'ID of the external calendar account.'
                                )
                                .optional()
                                .nullable(),
                              name: z
                                .string()
                                .describe(
                                  'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'You can import events from the list of specified external calendar\naccounts. The list may include the primary calendar.'
                      ),
                  }),
                ])
              )
              .describe(
                'Configuration for importing events from the external calendar to the Wix\nsite.'
              )
              .optional(),
            syncToCalendar: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether Wix [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nare exported to the external calendar.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Configuration for exporting events from the Wix site to the external\ncalendar.'
              )
              .optional(),
          })
          .describe('Settings about which sync operations are supported.')
          .optional(),
      })
    )
    .optional(),
  failedProviderIds: z.array(z.string()).optional(),
});
export const ConnectByOAuthRequest = z.object({
  providerId: z
    .string()
    .describe(
      'ID of the external calendar provider. Find this with the `listProviders()` function.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  scheduleId: z
    .string()
    .describe(
      'ID of the schedule to connect with the external calendar account.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  redirectUrl: z
    .string()
    .describe(
      'URL to redirect the user to after they authorize access to the external calendar account.\n\nIf the connection is successfully established, the user is redirected to this URL with a query parameter `connectionId` containing the new connection ID.\nIf the attempt to connect fails, the user is redirected to this URL with a query parameter `error` containing the error type.'
    )
    .url(),
});
export const ConnectByOAuthResponse = z.object({
  oauthUrl: z
    .string()
    .describe(
      'URL of the external calendar authorization page to redirect the user to.'
    )
    .url()
    .optional()
    .nullable(),
});
export const ConnectByCredentialsRequest = z.object({
  providerId: z
    .string()
    .describe(
      'ID of the external calendar provider. Find this with the `listProviders()` function.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  scheduleId: z
    .string()
    .describe(
      'ID of the schedule to connect with the external calendar account.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  email: z
    .string()
    .describe('Email address for the external calendar account.')
    .email(),
  password: z.string().describe('Password for the external calendar account.'),
});
export const ConnectByCredentialsResponse = z.object({
  connection: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the connection between the external calendar and the Wix site.'
        )
        .optional()
        .nullable(),
      providerId: z
        .string()
        .describe('ID of the external calendar provider.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      calendarType: z
        .enum([
          'UNDEFINED',
          'GOOGLE',
          'I_CAL',
          'OUTLOOK',
          'OFFICE_365',
          'MICROSOFT',
          'OTHER',
        ])
        .describe('External calendar type.')
        .optional(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nthat's connected to the external calendar."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      userId: z
        .string()
        .describe(
          'ID of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the external calendar connection belongs.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app which created the external calendar connection.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalAccountEmail: z
        .string()
        .describe(
          'Email address associated with the external calendar account. Available only\nafter completed successfully.'
        )
        .email()
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNDEFINED',
          'CONNECTED',
          'SYNC_IN_PROGRESS',
          'SYNCED',
          'DISCONNECTED',
          'ERROR',
        ])
        .describe('Connection status.')
        .optional(),
      errorReason: z
        .enum([
          'UNDEFINED',
          'TOKEN_REVOKED',
          'EXTERNAL_CALENDAR_CREATION_FAILED',
          'EXTERNAL_CALENDAR_DELETED',
        ])
        .describe(
          'Reason for the error. Available only if `status` is `ERROR`.'
        )
        .optional(),
      syncConfig: z
        .object({
          listEventFromCalendars: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether you can call [List Events](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/list-events)\nto import events from the external calendar to the Wix site.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({ calendars: z.never().optional() }),
                z.object({
                  calendars: z
                    .object({
                      calendars: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('ID of the external calendar account.')
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe(
                                'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe(
                      'You can import events from the list of specified external calendar\naccounts. The list may include the primary calendar.'
                    ),
                }),
              ])
            )
            .describe(
              'Configuration for importing events from the external calendar to the Wix\nsite.'
            )
            .optional(),
          syncToCalendar: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether Wix [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nare exported to the external calendar.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Configuration for exporting events from the Wix site to the external\ncalendar.'
            )
            .optional(),
        })
        .describe('Settings about which sync operations are supported.')
        .optional(),
    })
    .describe('Established connection details.')
    .optional(),
});
export const ListCalendarsRequest = z.object({
  connectionId: z
    .string()
    .describe('ID of the external calendar connection to list calendars for.'),
});
export const ListCalendarsResponse = z.object({
  calendars: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('ID of the external calendar account.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const UpdateSyncConfigRequest = z.object({
  connectionId: z
    .string()
    .describe('ID of the external calendar connection to update.'),
  syncConfig: z
    .object({
      listEventFromCalendars: z
        .intersection(
          z.object({
            enabled: z
              .boolean()
              .describe(
                'Whether you can call [List Events](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/list-events)\nto import events from the external calendar to the Wix site.'
              )
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({ calendars: z.never().optional() }),
            z.object({
              calendars: z
                .object({
                  calendars: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('ID of the external calendar account.')
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe(
                            'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                })
                .describe(
                  'You can import events from the list of specified external calendar\naccounts. The list may include the primary calendar.'
                ),
            }),
          ])
        )
        .describe(
          'Configuration for importing events from the external calendar to the Wix\nsite.'
        )
        .optional(),
      syncToCalendar: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether Wix [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nare exported to the external calendar.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Configuration for exporting events from the Wix site to the external\ncalendar.'
        )
        .optional(),
    })
    .describe('Updated sync configuration details.'),
});
export const UpdateSyncConfigResponse = z.object({
  connection: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the connection between the external calendar and the Wix site.'
        )
        .optional()
        .nullable(),
      providerId: z
        .string()
        .describe('ID of the external calendar provider.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      calendarType: z
        .enum([
          'UNDEFINED',
          'GOOGLE',
          'I_CAL',
          'OUTLOOK',
          'OFFICE_365',
          'MICROSOFT',
          'OTHER',
        ])
        .describe('External calendar type.')
        .optional(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nthat's connected to the external calendar."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      userId: z
        .string()
        .describe(
          'ID of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the external calendar connection belongs.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app which created the external calendar connection.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalAccountEmail: z
        .string()
        .describe(
          'Email address associated with the external calendar account. Available only\nafter completed successfully.'
        )
        .email()
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNDEFINED',
          'CONNECTED',
          'SYNC_IN_PROGRESS',
          'SYNCED',
          'DISCONNECTED',
          'ERROR',
        ])
        .describe('Connection status.')
        .optional(),
      errorReason: z
        .enum([
          'UNDEFINED',
          'TOKEN_REVOKED',
          'EXTERNAL_CALENDAR_CREATION_FAILED',
          'EXTERNAL_CALENDAR_DELETED',
        ])
        .describe(
          'Reason for the error. Available only if `status` is `ERROR`.'
        )
        .optional(),
      syncConfig: z
        .object({
          listEventFromCalendars: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether you can call [List Events](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/list-events)\nto import events from the external calendar to the Wix site.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({ calendars: z.never().optional() }),
                z.object({
                  calendars: z
                    .object({
                      calendars: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('ID of the external calendar account.')
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe(
                                'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe(
                      'You can import events from the list of specified external calendar\naccounts. The list may include the primary calendar.'
                    ),
                }),
              ])
            )
            .describe(
              'Configuration for importing events from the external calendar to the Wix\nsite.'
            )
            .optional(),
          syncToCalendar: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether Wix [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nare exported to the external calendar.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Configuration for exporting events from the Wix site to the external\ncalendar.'
            )
            .optional(),
        })
        .describe('Settings about which sync operations are supported.')
        .optional(),
    })
    .describe('Connection with updated sync configuration.')
    .optional(),
});
export const DisconnectRequest = z.object({
  connectionId: z
    .string()
    .describe('ID of the external calendar connection to disconnect.'),
});
export const DisconnectResponse = z.object({
  connection: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the connection between the external calendar and the Wix site.'
        )
        .optional()
        .nullable(),
      providerId: z
        .string()
        .describe('ID of the external calendar provider.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      calendarType: z
        .enum([
          'UNDEFINED',
          'GOOGLE',
          'I_CAL',
          'OUTLOOK',
          'OFFICE_365',
          'MICROSOFT',
          'OTHER',
        ])
        .describe('External calendar type.')
        .optional(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nthat's connected to the external calendar."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      userId: z
        .string()
        .describe(
          'ID of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the external calendar connection belongs.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app which created the external calendar connection.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalAccountEmail: z
        .string()
        .describe(
          'Email address associated with the external calendar account. Available only\nafter completed successfully.'
        )
        .email()
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNDEFINED',
          'CONNECTED',
          'SYNC_IN_PROGRESS',
          'SYNCED',
          'DISCONNECTED',
          'ERROR',
        ])
        .describe('Connection status.')
        .optional(),
      errorReason: z
        .enum([
          'UNDEFINED',
          'TOKEN_REVOKED',
          'EXTERNAL_CALENDAR_CREATION_FAILED',
          'EXTERNAL_CALENDAR_DELETED',
        ])
        .describe(
          'Reason for the error. Available only if `status` is `ERROR`.'
        )
        .optional(),
      syncConfig: z
        .object({
          listEventFromCalendars: z
            .intersection(
              z.object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether you can call [List Events](https://dev.wix.com/docs/rest/business-solutions/bookings/calendar/external-calendar-v2/list-events)\nto import events from the external calendar to the Wix site.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({ calendars: z.never().optional() }),
                z.object({
                  calendars: z
                    .object({
                      calendars: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('ID of the external calendar account.')
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe(
                                'Display name of the external calendar account.\nFor example, `Primary` or `Birthdays`.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .optional(),
                    })
                    .describe(
                      'You can import events from the list of specified external calendar\naccounts. The list may include the primary calendar.'
                    ),
                }),
              ])
            )
            .describe(
              'Configuration for importing events from the external calendar to the Wix\nsite.'
            )
            .optional(),
          syncToCalendar: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether Wix [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)\nare exported to the external calendar.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Configuration for exporting events from the Wix site to the external\ncalendar.'
            )
            .optional(),
        })
        .describe('Settings about which sync operations are supported.')
        .optional(),
    })
    .describe('Updated connection details.')
    .optional(),
});
export const ListEventsRequest = z.object({
  options: z
    .object({
      from: z
        .string()
        .describe(
          'Date and time from which to retrieve events,\nformatted according to [RFC 3339](https://www.ietf.org/rfc/rfc3339.txt).\nRequired, unless `cursorPaging.cursor` is provided.\n\nEvents which start before the `from` time and end after it are included in the returned list.'
        )
        .optional()
        .nullable(),
      to: z
        .string()
        .describe(
          'Date and time until which to retrieve events,\nformatted according to [RFC 3339](https://www.ietf.org/rfc/rfc3339.txt).\nRequired, unless `cursorPaging.cursor` is provided.\n\nEvents which start before the `to` time and end after it are included in the returned list.'
        )
        .optional()
        .nullable(),
      scheduleIds: z.array(z.string()).max(100).optional(),
      userIds: z.array(z.string()).max(100).optional(),
      allDay: z
        .boolean()
        .describe(
          'Whether to include only all-day events in the returned list.\nIf `true`, only all-day events are returned.\nIf `false`, only events with a specified time are returned.\n\nDefault: All events are returned.'
        )
        .optional()
        .nullable(),
      fieldsets: z.array(z.string()).optional(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of events to load.\nMax: `1000`')
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      partialFailure: z
        .boolean()
        .describe(
          "Whether to return a partial list of events if details can't be retrieved for some connections.\n\nDefault: `false`"
        )
        .optional()
        .nullable(),
    })
    .describe('Options to use when listing events.')
    .optional(),
});
export const ListEventsResponse = z.object({
  events: z
    .array(
      z.object({
        scheduleId: z
          .string()
          .describe(
            'ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nto which the event belongs.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        calendarType: z
          .enum([
            'UNDEFINED',
            'GOOGLE',
            'I_CAL',
            'OUTLOOK',
            'OFFICE_365',
            'MICROSOFT',
            'OTHER',
          ])
          .describe('External calendar type.')
          .optional(),
        calendarName: z
          .string()
          .describe(
            'Display name of the external calendar.\nFor example, `Primary` or `Birthdays`.'
          )
          .optional()
          .nullable(),
        title: z.string().describe('Event title.').optional().nullable(),
        start: z
          .string()
          .describe(
            'Start date and time of the event (inclusive), formatted according to [RFC 3339](https://www.ietf.org/rfc/rfc3339.txt).'
          )
          .optional()
          .nullable(),
        end: z
          .string()
          .describe(
            'End date and time of the event (exclusive), formatted according to [RFC 3339](https://www.ietf.org/rfc/rfc3339.txt).'
          )
          .optional()
          .nullable(),
        allDay: z
          .boolean()
          .describe(
            'Whether the event is an all-day event.\n\nDefault: `false`'
          )
          .optional()
          .nullable(),
        scheduleOwnerId: z
          .string()
          .describe(
            'ID of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the schedule belongs.\nFor Bookings [staff members](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction)\nidentical to their [resource ID](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction).'
          )
          .optional()
          .nullable(),
        scheduleOwnerName: z
          .string()
          .describe(
            'Name of the [Wix user](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#wix-user)\nto whom the schedule belongs.\nFor example, the `name` of a Bookings [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction).'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
  failedProviderIds: z.array(z.string()).optional(),
});
